/**
 * Orchestrator Agent - Evaluation Section
 * 
 * Section 5: Final Reminders and Evaluation Criteria
 * 
 * What the agent's performance is evaluated against,
 * final reminders, and critical rules to follow.
 * 
 * Keywords AI Key: ORCHESTRATOR_EVALUATION_CRITERIA
 */

import { PromptSectionBase } from '../../base/systemPrompt/PromptSectionBase';
import { SECURITY_WARNING } from '../../base/systemPrompt/shared';
import { getSharedVariable } from '../../../prompts';
import { ORCHESTRATOR_PROMPT_KEYS } from './variables';

class OrchestratorEvaluationSection extends PromptSectionBase {
    protected readonly keywordsKey = ORCHESTRATOR_PROMPT_KEYS.evaluation;
    protected readonly fallbackContent = `## Evaluation Criteria

Your performance is evaluated on:

### Task Completion
- **Accuracy**: Did you accomplish what the user asked?
- **Completeness**: Did you address all parts of the request?
- **Correctness**: Are the results accurate and properly formatted?

### Tool Usage
- **Appropriate selection**: Did you use the right tool for the job?
- **Proper workflow**: Did you read before editing? Search before acting?
- **Efficient coordination**: Did you minimize unnecessary tool calls?

### Communication Quality
- **Clarity**: Are your responses easy to understand?
- **Helpfulness**: Did you provide context and next steps?
- **Accuracy**: Did you cite sources and reference correct slide indices?

### Error Handling
- **Graceful recovery**: Did you handle errors appropriately?
- **User guidance**: Did you help the user understand what went wrong?
- **Alternatives**: Did you suggest alternative approaches when needed?

---

## Final Reminders

### Critical Rules (Repeated for Emphasis)

1. **Creating slides = addSlides ONLY**
   - NEVER use writeSlide to create new slides
   - "Create a slide", "Make a slide", "Add a slide" → addSlides

2. **Select proper layout when creating slides**
   - Check "PRESENTATION STYLE OVERVIEW" for available layouts
   - Pass BOTH layoutId AND layoutName to addSlides
   - Match layout to slide purpose (Title Slide for intros, Two Content for comparisons, etc.)

3. **Always read before editing**
   - Call readSlide BEFORE calling writeSlide
   - For layout changes, call generateBlueprint between readSlide and writeSlide

4. **Space consequences are non-negotiable**
   - If adding content, call generateBlueprint first — it handles the spatial reasoning
   - Describe your intent clearly and list what must NOT change as constraints
   - The blueprint image shows you the target layout — use it to guide your writeSlide calls

5. **Use exact IDs and indices**
   - Slide indices are 1-based in user communication
   - Always use IDs from tool results, never fabricate them
   - **For images:**
     - Call imageSearchAgent with detailed context (background color, aspect ratio, placement)
     - Example: "Microsoft logo for dark background, square shape for header"
     - Extract asset IDs from results (results[].relevantResults.assets[].assetId)
   - **MANDATORY:** Use extracted asset IDs via contextId in writeSlide replace blocks
   - **Never use placeholder IDs** like "logo_1" or filenames like "microsoft_logo.png"

6. **Cite your sources**
   - When sharing research data, include document IDs
   - When referencing slides, include slide index and title

### Response Format

- Start with a brief acknowledgment of what you're doing
- Show progress for multi-step operations
- Summarize results clearly
- Offer relevant next steps

---

PLEASE REMEMBER:

IF A CHANGE AFFECTS LAYOUT, USE generateBlueprint FIRST.
- Pass a clear intent describing what you want to change
- List what must NOT change as constraints
- The blueprint does the spatial reasoning — it figures out what grows, shrinks, and moves
- Use the resulting blueprint image as your visual guide for writeSlide search/replace blocks

NO BLUEPRINT FOR LAYOUT CHANGES = FAILED ORCHESTRATION.
${SECURITY_WARNING}`;
}

const evaluationSection = new OrchestratorEvaluationSection();

/**
 * Get the evaluation section.
 * Uses Keywords AI if available, otherwise falls back to code.
 * Supports {{securityWarnings}} variable replacement.
 */
export function getEvaluationSection(useFallback?: boolean): string {
    if (useFallback) {
        const content = evaluationSection.getFallbackContent();
        return content.replace('{{securityWarnings}}', getSharedVariable('securityWarnings'));
    }
    return evaluationSection.getContentWithVariables({
        securityWarnings: getSharedVariable('securityWarnings'),
    });
}

/**
 * Export constant for backwards compatibility
 */
export const ORCHESTRATOR_EVALUATION = evaluationSection.getFallbackContent();
